import { useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import { Sun, Loader2, Sparkles, Pencil } from "lucide-react";
import api, { errMsg } from "../../lib/api";
import SEO from "../../components/SEO";
import { SelectF, StatusBadge, AI_MODELS } from "../admin/AFields";
import { STYLE_THEMES } from "./StudioCard";
import { pickLocalized } from "./localized";

export default function StudioDaily() {
  const { base } = useOutletContext();
  const [opts, setOpts] = useState({ language: "bilingual", style: "", model: "gpt-5.4-mini" });
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/greetings/list").then((r) => setItems(r.data.filter((g) => g.is_daily))).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);

  const generate = async () => {
    setBusy(true);
    try {
      const r = await api.post("/studio/daily/generate", { ...opts, style: opts.style || undefined });
      toast.success(`Draft generated — style: ${r.data.style}. Review and publish.`);
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div data-testid="studio-daily">
      <SEO title="Today's Greeting" />
      <h1 className="font-display text-2xl font-bold tracking-tight text-navy sm:text-3xl">Today's Greeting</h1>
      <p className="mt-1 text-sm text-slate-500">AI rotates the visual style daily — no design repeats within the last 7 days. Every greeting stays a draft until you approve it.</p>

      <div className="mt-6 max-w-2xl rounded-lg border border-gray-200 bg-white p-6" data-testid="daily-generator">
        <div className="grid gap-4 sm:grid-cols-3">
          <SelectF label="Language" value={opts.language} onChange={(v) => setOpts({ ...opts, language: v })} options={["english", "hindi", "bilingual"]} />
          <SelectF label="Style (blank = auto-rotate)" value={opts.style} onChange={(v) => setOpts({ ...opts, style: v })} options={["", ...Object.keys(STYLE_THEMES)]} />
          <div>
            <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">AI Model</label>
            <select value={opts.model} onChange={(e) => setOpts({ ...opts, model: e.target.value })} className="w-full rounded-md border border-gray-200 bg-white px-3.5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-saffron-500/50" data-testid="daily-model">
              {AI_MODELS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
        </div>
        <button onClick={generate} disabled={busy} className="btn-primary mt-5 !rounded-md !px-6 !py-2.5 text-sm disabled:opacity-50" data-testid="daily-generate-page-btn">
          {busy ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />} Generate Today's Draft
        </button>
      </div>

      <div className="mt-8">
        <h2 className="flex items-center gap-2 font-display text-lg font-bold text-navy"><Sun size={17} className="text-saffron-600" /> Recent Daily Greetings</h2>
        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((g) => (
            <div key={g.id} className="rounded-lg border border-gray-200 bg-white p-4" data-testid={`daily-row-${g.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="truncate text-sm font-bold text-navy">{pickLocalized(g.title)}</p>
                  <p className="mt-0.5 text-xs text-slate-500">{g.date} · style: {g.style || "—"} · {pickLocalized(g.language)}</p>
                </div>
                <StatusBadge value={g.status} />
              </div>
              <p className="mt-2 line-clamp-2 text-xs text-slate-600">{pickLocalized(g.message)}</p>
              <Link to={`${base}/create?id=${g.id}`} className="mt-3 inline-flex items-center gap-1.5 text-xs font-bold text-saffron-600 hover:text-navy" data-testid={`daily-edit-${g.id}`}><Pencil size={12} /> Review & Edit</Link>
            </div>
          ))}
          {items.length === 0 && <p className="col-span-full rounded-lg border border-dashed border-gray-300 py-10 text-center text-sm text-slate-400">No daily greetings yet — generate one above.</p>}
        </div>
      </div>
    </div>
  );
}
